import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const Footer = () => {
  const currentYear = new Date()?.getFullYear();

  const footerSections = [
    {
      title: "Products",
      links: [
        { name: "Endoscopy Equipment", href: "/product-portfolio?category=endoscopy" },
        { name: "Surgical Instruments", href: "/product-portfolio?category=surgery" },
        { name: "Obesity Management", href: "/product-portfolio?category=obesity" },
        { name: "Accessories", href: "/product-portfolio?category=accessories" }
      ]
    },
    {
      title: "Services",
      links: [
        { name: "Technical Support", href: "/contact" },
        { name: "Training Programs", href: "/contact" },
        { name: "Maintenance", href: "/contact" },
        { name: "Consultation", href: "/contact" }
      ]
    },
    {
      title: "Company",
      links: [
        { name: "About Us", href: "/about-us" },
        { name: "Our Team", href: "/about-us#team" },
        { name: "Certifications", href: "/about-us#certifications" },
        { name: "Contact", href: "/contact" }
      ]
    },
    {
      title: "Resources",
      links: [
        { name: "Product Catalogs", href: "/product-portfolio" },
        { name: "Technical Guides", href: "/contact" },
        { name: "Training Materials", href: "/contact" },
        { name: "Support Center", href: "/contact" }
      ]
    }
  ];

  const socialLinks = [
    { name: "LinkedIn", icon: "Linkedin", href: "https://linkedin.com/company/endosurgery-malaysia" },
    { name: "Facebook", icon: "Facebook", href: "https://facebook.com/endosurgerymalaysia" },
    { name: "Twitter", icon: "Twitter", href: "https://twitter.com/endosurgery_my" },
    { name: "YouTube", icon: "Youtube", href: "https://youtube.com/@endosurgerymalaysia" }
  ];

  const certifications = [
    { name: "GDPMD Certified", icon: "Shield" },
    { name: "ISO 13485", icon: "CheckCircle" },
    { name: "CE Mark", icon: "Award" },
    { name: "FDA Approved", icon: "Verified" }
  ];

  return (
    <footer className="bg-secondary text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            {/* Logo */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center p-2">
                <Image
                  src="/assets/images/Endo-Logo-small.png"
                  alt="Endosurgery Malaysia Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="flex flex-col">
                <span className="text-lg font-semibold leading-tight">
                  Endosurgery
                </span>
                <span className="text-xs font-medium text-white/70 leading-tight">
                  Malaysia
                </span>
              </div>
            </div>

            <p className="text-white/80 leading-relaxed mb-4 text-sm">
              Malaysia's leading distributor of endoscopic and surgical products, 
              serving medical professionals with GDPMD certified solutions since 2012.
            </p>

            {/* Contact Info */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={14} color="var(--color-primary)" />
                <span className="text-xs text-white/80">
                  Level 12, Menara Axiata, KL Sentral, 50470 Kuala Lumpur
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Phone" size={14} color="var(--color-primary)" />
                <span className="text-xs text-white/80">+60 3-2274 8888</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Mail" size={14} color="var(--color-primary)" />
                <span className="text-xs text-white/80">info@endosurgery.com.my</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex space-x-3">
              {socialLinks?.map((social, index) => (
                <a
                  key={index}
                  href={social?.href}
                  className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center hover:bg-primary transition-colors duration-200"
                  aria-label={social?.name}
                >
                  <Icon name={social?.icon} size={16} />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Footer Links - Simplified */}
          {footerSections?.slice(0, 2)?.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-sm font-semibold mb-3">{section?.title}</h3>
              <ul className="space-y-1">
                {section?.links?.slice(0, 3)?.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a
                      href={link?.href}
                      className="text-white/70 hover:text-primary transition-colors duration-200 text-xs"
                    >
                      {link?.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Certifications - Simplified */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-6 pt-4 border-t border-white/10"
        >
          <div className="flex flex-wrap justify-center gap-4">
            <span className="text-xs text-white/60">Certified:</span>
            {certifications?.slice(0, 2)?.map((cert, index) => (
              <div key={index} className="flex items-center space-x-1">
                <Icon name={cert?.icon} size={12} color="var(--color-success)" />
                <span className="text-xs text-white/80">{cert?.name}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
      {/* Bottom Footer */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-8 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
            <div className="text-xs text-white/60">
              © {currentYear} Endosurgery Sdn Bhd. All rights reserved.
            </div>
            
            <div className="flex flex-wrap gap-4 text-xs text-white/60">
              <a href="/privacy-policy" className="hover:text-primary transition-colors duration-200">
                Privacy Policy
              </a>
              <a href="/terms-of-service" className="hover:text-primary transition-colors duration-200">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;